import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, ScatterChart, Scatter } from 'recharts';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
import { Search, Filter, Download, Eye, TrendingDown, TrendingUp } from 'lucide-react';

/**
 * Interactive Scenario Explorer Component
 * Phase 2: Detailed scenario analysis with filtering, search, and drill-down capabilities
 */
const ScenarioExplorerSubtab = ({ 
    simulationResults,
    isDarkMode = false 
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [sortBy, setSortBy] = useState('scenario'); // 'scenario', 'projectNPV', 'impact'
    const [filterBy, setFilterBy] = useState('all'); // 'all', 'extreme', 'normal', 'category'
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Process scenarios for display
    const processedScenarios = useMemo(() => {
        if (!simulationResults?.scenarios) return [];

        let scenarios = simulationResults.scenarios.map(scenario => ({
            ...scenario,
            impactDescription: scenario.extremeEvent ? 
                `${scenario.extremeEvent.event.name} (${((1 - scenario.extremeEvent.impactMultiplier) * 100).toFixed(1)}% impact)` : 
                'Normal market conditions',
            categoryId: scenario.extremeEvent?.categoryId || 'NORMAL',
            severity: scenario.extremeEvent ? getSeverityLevel(1 - scenario.extremeEvent.impactMultiplier) : 'Normal'
        }));

        // Apply filters
        if (filterBy === 'extreme') {
            scenarios = scenarios.filter(s => s.hasExtremeEvent);
        } else if (filterBy === 'normal') {
            scenarios = scenarios.filter(s => !s.hasExtremeEvent);
        } else if (filterBy === 'category' && selectedCategory !== 'all') {
            scenarios = scenarios.filter(s => s.categoryId === selectedCategory);
        }

        // Apply search
        if (searchTerm) {
            scenarios = scenarios.filter(scenario => 
                scenario.impactDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
                scenario.scenario.toString().includes(searchTerm)
            );
        }

        // Apply sorting
        scenarios.sort((a, b) => {
            switch (sortBy) {
                case 'projectNPV':
                    return (b.projectNPV || 0) - (a.projectNPV || 0);
                case 'impact':
                    const aImpact = a.extremeEvent ? (1 - a.extremeEvent.impactMultiplier) : 0;
                    const bImpact = b.extremeEvent ? (1 - b.extremeEvent.impactMultiplier) : 0;
                    return bImpact - aImpact;
                default:
                    return a.scenario - b.scenario;
            }
        });

        return scenarios;
    }, [simulationResults, searchTerm, sortBy, filterBy, selectedCategory]);

    // Statistical summary of filtered scenarios
    const summary = useMemo(() => {
        if (processedScenarios.length === 0) return null;

        const npvValues = processedScenarios.map(s => s.projectNPV).filter(v => v !== null);
        const extremeScenarios = processedScenarios.filter(s => s.hasExtremeEvent);

        return {
            totalScenarios: processedScenarios.length,
            extremeEventCount: extremeScenarios.length,
            extremeEventRate: extremeScenarios.length / processedScenarios.length,
            avgNPV: npvValues.reduce((sum, val) => sum + val, 0) / npvValues.length,
            minNPV: Math.min(...npvValues),
            maxNPV: Math.max(...npvValues),
            worstScenario: processedScenarios.find(s => s.projectNPV === Math.min(...npvValues)),
            bestScenario: processedScenarios.find(s => s.projectNPV === Math.max(...npvValues))
        };
    }, [processedScenarios]);

    const getSeverityLevel = (impact) => {
        if (impact < 0.1) return 'Low';
        else if (impact < 0.3) return 'Medium';
        else if (impact < 0.6) return 'High';
        else if (impact < 0.8) return 'Critical';
        else return 'Catastrophic';
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'Low': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/20';
            case 'Medium': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/20';
            case 'High': return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/20';
            case 'Critical': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/20';
            case 'Catastrophic': return 'text-red-800 dark:text-red-300 bg-red-200 dark:bg-red-900/40';
            default: return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20';
        }
    };

    const getCategoryDisplayName = (categoryId) => {
        const names = {
            FINANCIAL_RISK: '🏦 Financial Risk',
            STRATEGIC_RISK: '📈 Strategic Risk', 
            HAZARD_RISK: '⚡ Hazard Risk',
            OPERATIONS_RISK: '⚙️ Operations Risk',
            EXTREME_BLACK_SWAN: '🌋 Black Swan',
            NORMAL: '📊 Normal Market'
        };
        return names[categoryId] || categoryId;
    };

    if (!simulationResults?.scenarios || simulationResults.scenarios.length === 0) {
        return (
            <div className="text-center p-8">
                <div className="text-4xl mb-4">🎲</div>
                <h3 className="text-lg font-semibold mb-2">No Scenarios Available</h3>
                <p className="text-gray-600 dark:text-gray-400">
                    Run an extreme events simulation to explore individual scenarios.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Controls */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap items-center gap-4">
                    {/* Search */}
                    <div className="flex items-center space-x-2">
                        <Search className="h-4 w-4 text-gray-500" />
                        <input
                            type="text"
                            placeholder="Search scenarios..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md 
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                    </div>

                    {/* Filter */}
                    <div className="flex items-center space-x-2">
                        <Filter className="h-4 w-4 text-gray-500" />
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md 
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                            <option value="all">All Scenarios</option>
                            <option value="extreme">Extreme Events Only</option>
                            <option value="normal">Normal Scenarios Only</option>
                            <option value="category">By Category</option>
                        </select>
                        {filterBy === 'category' && (
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md 
                                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            >
                                <option value="all">All Categories</option>
                                <option value="FINANCIAL_RISK">Financial Risk</option>
                                <option value="STRATEGIC_RISK">Strategic Risk</option>
                                <option value="HAZARD_RISK">Hazard Risk</option>
                                <option value="OPERATIONS_RISK">Operations Risk</option>
                                <option value="EXTREME_BLACK_SWAN">Black Swan</option>
                            </select>
                        )}
                    </div>

                    {/* Sort */}
                    <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Sort by:</span>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md 
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                            <option value="scenario">Scenario #</option>
                            <option value="projectNPV">Project NPV</option>
                            <option value="impact">Impact Level</option>
                        </select>
                    </div>

                    {/* Export */}
                    <button className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
                        <Download className="h-4 w-4" />
                        <span>Export</span>
                    </button>
                </div>
            </div>

            {/* Summary Statistics */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Total Scenarios</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                            {summary.totalScenarios.toLocaleString()}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Extreme Events</div>
                        <div className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                            {summary.extremeEventCount} ({formatPercentage(summary.extremeEventRate * 100)})
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Average NPV</div>
                        <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                            {formatCurrency(summary.avgNPV)}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Best Case</div>
                        <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                            {formatCurrency(summary.maxNPV)}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Worst Case</div>
                        <div className="text-lg font-semibold text-red-600 dark:text-red-400">
                            {formatCurrency(summary.minNPV)}
                        </div>
                    </div>
                </div>
            )}

            {/* Scenarios List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Scenario Details ({processedScenarios.length} scenarios)
                    </h3>
                </div>

                <div className="max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Scenario
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Event/Condition
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Project NPV
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Project IRR
                                </th>
                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {processedScenarios.slice(0, 100).map((scenario) => (
                                <tr 
                                    key={scenario.scenario}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                                    onClick={() => setSelectedScenario(scenario)}
                                >
                                    <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white">
                                        #{scenario.scenario}
                                    </td>
                                    <td className="px-4 py-2">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(scenario.severity)}`}>
                                            {scenario.severity}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                                        {getCategoryDisplayName(scenario.categoryId)} - {scenario.impactDescription}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-right font-medium">
                                        <span className={scenario.projectNPV >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                            {formatCurrency(scenario.projectNPV)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-right">
                                        {formatPercentage(scenario.projectIRR)}
                                    </td>
                                    <td className="px-4 py-2 text-center">
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedScenario(scenario);
                                            }}
                                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                        >
                                            <Eye className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {processedScenarios.length > 100 && (
                        <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                            Showing first 100 scenarios. Use filters to narrow down results.
                        </div>
                    )}
                </div>
            </div>

            {/* Selected Scenario Details */}
            {selectedScenario && (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Scenario #{selectedScenario.scenario} Details
                        </h3>
                        <button 
                            onClick={() => setSelectedScenario(null)}
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                            ×
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Scenario Information */}
                        <div>
                            <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">Scenario Information</h4>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Type:</span>
                                    <span className={`px-2 py-1 rounded-full text-xs ${getSeverityColor(selectedScenario.severity)}`}>
                                        {selectedScenario.severity}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Category:</span>
                                    <span>{getCategoryDisplayName(selectedScenario.categoryId)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Description:</span>
                                    <span className="text-right max-w-xs">{selectedScenario.impactDescription}</span>
                                </div>
                                {selectedScenario.extremeEvent && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Impact Multiplier:</span>
                                        <span className="text-red-600 dark:text-red-400">
                                            {selectedScenario.extremeEvent.impactMultiplier.toFixed(3)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Financial Metrics */}
                        <div>
                            <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">Financial Metrics</h4>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Project NPV:</span>
                                    <span className={selectedScenario.projectNPV >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                        {formatCurrency(selectedScenario.projectNPV)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Capital NPV:</span>
                                    <span className={selectedScenario.capitalNPV >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                        {formatCurrency(selectedScenario.capitalNPV)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Project IRR:</span>
                                    <span>{formatPercentage(selectedScenario.projectIRR)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Capital IRR:</span>
                                    <span>{formatPercentage(selectedScenario.capitalIRR)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">LCOE:</span>
                                    <span>{formatCurrency(selectedScenario.lcoe, 4)}/kWh</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScenarioExplorerSubtab;